import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { startOfWeek, endOfWeek, isWithinInterval, differenceInDays } from 'date-fns';

export type DashboardRole = 'default' | 'content' | 'ads';

export interface Notification {
  id: string;           // unique key, e.g. "pending_videos:release_abc"
  type: 'pending_videos' | 'meta_access' | 'no_creatives' | 'content_call' | 'campaign_ending';
  projectId: string;
  artistName: string;
  releaseName: string;
  message: string;
  role: 'content' | 'ads'; // which role this notification belongs to
}

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(new Set());
  const [dashboardRole, setDashboardRole] = useState<DashboardRole>('default');
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();

  // Load user's dashboard role
  useEffect(() => {
    if (!currentUser) return;
    supabase
      .from('profiles')
      .select('dashboard_role')
      .eq('id', currentUser.id)
      .single()
      .then(({ data }) => {
        setDashboardRole((data?.dashboard_role as DashboardRole) || 'default');
      });
  }, [currentUser]);

  // Load dismissed notifications
  const fetchDismissals = useCallback(async () => {
    if (!currentUser) return;
    const { data } = await supabase
      .from('notification_dismissals')
      .select('notification_key')
      .eq('user_id', currentUser.id);
    if (data) {
      setDismissedKeys(new Set(data.map((d: any) => d.notification_key)));
    }
  }, [currentUser]);

  useEffect(() => { fetchDismissals(); }, [fetchDismissals]);

  const fetchNotifications = useCallback(async () => {
    if (!currentUser) return;

    try {
      const { data: projects, error } = await supabase
        .from('dashboard_projects')
        .select('id, release_id, artist_id, artist_name, release_name, project_status, service_package, pending_video_count, meta_access_granted, ad_creative_count, content_call_completed, release_date, campaign_end_date');

      if (error || !projects) {
        console.error('[Notifications] Failed to load dashboard projects:', error);
        return;
      }

      const now = new Date();
      const weekStart = startOfWeek(now, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
      const items: Notification[] = [];

      // Meta access is per-artist: if ANY project for an artist has it, all do
      const artistHasAccess = new Set<string>();
      for (const p of projects) {
        if (p.meta_access_granted && p.artist_id) {
          artistHasAccess.add(p.artist_id);
        }
      }
      const artistMetaNotified = new Set<string>();

      for (const p of projects) {
        // --- Content notifications ---

        // Pending videos (new / feedback-needed)
        if ((p.pending_video_count || 0) > 0 && p.service_package !== 'ad_only') {
          items.push({
            id: `pending_videos:${p.release_id}`,
            type: 'pending_videos',
            projectId: p.id,
            artistName: p.artist_name,
            releaseName: p.release_name,
            message: `${p.pending_video_count} Video${p.pending_video_count > 1 ? 's' : ''} zum Feedbacken`,
            role: 'content',
          });
        }

        // Content call not completed
        if (p.project_status === 'content_production' && !p.content_call_completed && p.service_package !== 'ad_only') {
          items.push({
            id: `content_call:${p.id}`,
            type: 'content_call',
            projectId: p.id,
            artistName: p.artist_name,
            releaseName: p.release_name,
            message: 'Content Call ausstehend',
            role: 'content',
          });
        }

        // Campaign ending soon (<=3 days)
        if (p.project_status === 'active_campaign' && p.campaign_end_date && p.service_package !== 'ad_only') {
          const daysLeft = differenceInDays(new Date(p.campaign_end_date), now);
          if (daysLeft >= 0 && daysLeft <= 3) {
            items.push({
              id: `campaign_ending:${p.id}`,
              type: 'campaign_ending',
              projectId: p.id,
              artistName: p.artist_name,
              releaseName: p.release_name,
              message: daysLeft === 0 ? 'Kampagne endet heute' : `Kampagne endet in ${daysLeft} Tag${daysLeft > 1 ? 'en' : ''}`,
              role: 'content',
            });
          }
        }

        // --- Ads notifications ---

        // Meta access is needed before the artist's release goes live. The
        // DB trigger may advance ad_only projects past onboarding early, so
        // use the release date rather than relying only on project_status.
        const releaseDate = p.release_date ? new Date(p.release_date) : null;
        const releaseNotLive = !releaseDate || releaseDate > now;
        if (
          releaseNotLive &&
          !artistHasAccess.has(p.artist_id) &&
          p.service_package !== 'content_only' &&
          !['completed', 'follow_up', 'paused'].includes(p.project_status) &&
          p.artist_id &&
          !artistMetaNotified.has(p.artist_id)
        ) {
          artistMetaNotified.add(p.artist_id);
          items.push({
            id: `meta_access:${p.artist_id}`,
            type: 'meta_access',
            projectId: p.id,
            artistName: p.artist_name,
            releaseName: p.release_name,
            message: 'Meta Access fehlt',
            role: 'ads',
          });
        }

        // 0 ad creatives in release week
        if (p.release_date && p.service_package !== 'content_only') {
          const releaseDate = new Date(p.release_date);
          if (isWithinInterval(releaseDate, { start: weekStart, end: weekEnd }) && (p.ad_creative_count || 0) === 0) {
            items.push({
              id: `no_creatives:${p.id}`,
              type: 'no_creatives',
              projectId: p.id,
              artistName: p.artist_name,
              releaseName: p.release_name,
              message: '0 Ad Creatives — Release diese Woche!',
              role: 'ads',
            });
          }
        }
      }

      if (import.meta.env.DEV) {
        console.debug('[Notifications] refreshed', {
          projects: projects.length,
          total: items.length,
          byType: items.reduce<Record<string, number>>((counts, item) => {
            counts[item.type] = (counts[item.type] || 0) + 1;
            return counts;
          }, {}),
        });
      }
      setNotifications(items);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Initial fetch + polling every 60s
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60_000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Realtime on submissions for instant video notification updates
  useEffect(() => {
    const channel = supabase
      .channel('notifications-submissions')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'submissions' }, () => {
        fetchNotifications();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchNotifications]);

  // Dismiss a notification
  const dismiss = useCallback(async (notificationId: string) => {
    if (!currentUser) return;
    setDismissedKeys(prev => new Set([...prev, notificationId]));
    await supabase.from('notification_dismissals').upsert({
      user_id: currentUser.id,
      notification_key: notificationId,
      dismissed_at: new Date().toISOString(),
    } as any, { onConflict: 'user_id,notification_key' });
  }, [currentUser]);

  // Undismiss a notification
  const undismiss = useCallback(async (notificationId: string) => {
    if (!currentUser) return;
    setDismissedKeys(prev => {
      const next = new Set(prev);
      next.delete(notificationId);
      return next;
    });
    await supabase
      .from('notification_dismissals')
      .delete()
      .eq('user_id', currentUser.id)
      .eq('notification_key', notificationId);
  }, [currentUser]);

  // Filter by role, then split into active vs dismissed
  const roleFiltered = dashboardRole === 'default'
    ? notifications
    : notifications.filter(n => n.role === dashboardRole);

  const activeNotifications = roleFiltered.filter(n => !dismissedKeys.has(n.id));
  const dismissedNotifications = roleFiltered.filter(n => dismissedKeys.has(n.id));

  return {
    notifications: activeNotifications,
    dismissedNotifications,
    allNotifications: notifications,
    count: activeNotifications.length,
    dashboardRole,
    loading,
    refresh: fetchNotifications,
    dismiss,
    undismiss,
  };
};
